import { useRef, type ChangeEvent } from "react";
import { ImagePlus, Layers, Paperclip, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import { TOUCH } from "~/shared/lib/design-tokens";
import { cn } from "~/shared/lib/utils";
import { Button } from "~/shared/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "~/shared/ui/dropdown-menu";

const TB = cn(TOUCH.secondary, "text-zinc-500 hover:text-zinc-300 hover:bg-white/5");

export interface AttachMenuProps {
    disabled: boolean;
    triggerDisabled: boolean;
    maxImages: number | null;
    currentImagesCount: number;
    onPickFiles: (files: File[]) => void;
    onPickFromUploaded?: () => void;
    pickFromUploadedDisabled?: boolean;
    onPickFromGenerated?: () => void;
    pickFromGeneratedDisabled?: boolean;
    accept?: string;
}

export function AttachMenu({
    disabled,
    triggerDisabled,
    maxImages,
    currentImagesCount,
    onPickFiles,
    onPickFromUploaded,
    pickFromUploadedDisabled,
    onPickFromGenerated,
    pickFromGeneratedDisabled,
    accept = "image/*",
}: AttachMenuProps) {
    const { t } = useTranslation("chat");
    const fileInputRef = useRef<HTMLInputElement>(null);

    const limitReached = maxImages != null && currentImagesCount >= maxImages;
    const uploadDisabled = disabled || limitReached;
    const limitLabel =
        maxImages == null
            ? t("prompt.attachMenu.noLimit")
            : t("prompt.attachMenu.limitImages", { count: maxImages });

    const handleFiles = (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        onPickFiles(Array.from(files));
        event.target.value = "";
    };

    const handleUploadClick = () => fileInputRef.current?.click();

    return (
        <>
            <input ref={fileInputRef} type="file" accept={accept} multiple hidden onChange={handleFiles} />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("prompt.attach")}
                        disabled={triggerDisabled}
                        className={TB}
                    >
                        <Paperclip className="h-4 w-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                    <DropdownMenuItem disabled={uploadDisabled} onClick={handleUploadClick}>
                        <ImagePlus className="mr-2 h-4 w-4" />
                        {t("prompt.attachMenu.uploadFile")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={!onPickFromUploaded || disabled || pickFromUploadedDisabled === true}
                        onClick={onPickFromUploaded}
                    >
                        <Layers className="mr-2 h-4 w-4" />
                        {t("prompt.attachMenu.fromUploaded")}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                        disabled={!onPickFromGenerated || disabled || pickFromGeneratedDisabled === true}
                        onClick={onPickFromGenerated}
                    >
                        <Sparkles className="mr-2 h-4 w-4" />
                        {t("prompt.attachMenu.fromGenerated")}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-zinc-500">{limitLabel}</DropdownMenuLabel>
                    {limitReached && (
                        <DropdownMenuLabel className="text-[11px] font-normal text-amber-400">
                            {t("prompt.attachMenu.limitReached")}
                        </DropdownMenuLabel>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
}
